<template>
    <custom-table
        :title="$t('pages.stock.warehouse.title')"
        :subTitle="$t('pages.stock.warehouse.subTitle')"
        rowKey="id"
        :items="table.data"
        :loading="table.loading"
        :columns="fields"
        :actions="actions"
        :pagination="table.pagination"
        changeUrlPage
        @action="handleClickAction"
        @changeTable="handleTableChange"
    >
        <template v-slot:name="{ row: record }">
            <div class="wh-name-cell">
                <span class="wh-name">{{ record.name }}</span>
                <span v-if="record.is_default" class="wh-default-pill">{{ $t('pages.cash_register.defaultBadge') }}</span>
            </div>
        </template>

        <template v-slot:shelves_count="{ row: record }">
            <span class="wh-count">{{ record.shelves_count ?? 0 }}</span>
        </template>

        <template v-slot:active="{ row: record }">
            <span class="wh-status" :class="record.active ? 'wh-status--on' : 'wh-status--off'">
                {{ record.active ? $t('common.active') : $t('common.passive') }}
            </span>
        </template>

        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <el-tooltip :content="$t('pages.stock.warehouse.shelf.title')" placement="top">
                    <router-link :to="'/stock/warehouse/' + record.id + '/shelf'" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                         <span class="svg-icon svg-icon-3">
                             <inline-svg src="/media/icons/duotune/files/fil001.svg"/>
                         </span>
                    </router-link>
                </el-tooltip>
                <el-tooltip :content="$t('common.edit')" placement="top">
                    <a v-on:click="fetchWarehouse(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                         <span class="svg-icon svg-icon-3">
                             <inline-svg src="/media/icons/duotune/art/art005.svg"/>
                         </span>
                    </a>
                </el-tooltip>
                <el-tooltip :content="canDelete ? $t('common.delete') : $t('pages.stock.errors.cannot_delete_last_warehouse')" placement="top">
                    <el-popconfirm v-if="canDelete"
                        :title="$t('messages.sureDelete')"
                        :confirm-button-text="$t('btn.yes')"
                        :cancel-button-text="$t('btn.no')"
                        @confirm="deleteRecord([record.id])">
                        <template #reference>
                            <a class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                                <span class="svg-icon svg-icon-3">
                                    <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                                </span>
                            </a>
                        </template>
                    </el-popconfirm>
                    <a v-else class="btn btn-icon btn-bg-light btn-sm wh-action--disabled">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                        </span>
                    </a>
                </el-tooltip>
            </div>
        </template>
    </custom-table>

    <div class="modal fade" id="kt_modal_warehouse" ref="warehouseModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-560px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ form.title }}</h2>
                    <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1"><inline-svg src="/media/icons/duotune/arrows/arr061.svg"/></span>
                    </div>
                </div>

                <el-form @submit.prevent="onSubmit()" :model="form" ref="warehouseForm">
                    <div class="modal-body py-8 px-lg-10">
                        <div class="wh-form-field">
                            <label class="wh-form-label wh-form-label--required">{{ $t('pages.stock.warehouse.cols.name') }}</label>
                            <el-form-item prop="data.name" :rules="$validation.getMessage(['required'])">
                                <el-input v-model="form.data.name" type="text"/>
                            </el-form-item>
                        </div>

                        <div v-if="form.updateStatus" class="wh-default-toggle">
                            <label class="wh-toggle-wrap" :class="{'wh-toggle-wrap--disabled': defaultToggleDisabled}">
                                <input type="checkbox"
                                    v-model="form.data.is_default"
                                    :disabled="defaultToggleDisabled"/>
                                <span class="wh-toggle-pill"></span>
                                <span class="wh-toggle-text">
                                    <span class="wh-toggle-title">{{ $t('pages.cash_register.setAsDefault') }}</span>
                                    <span v-if="defaultToggleDisabled" class="wh-toggle-hint">{{ $t('pages.stock.errors.at_least_one_default_warehouse_required') }}</span>
                                </span>
                            </label>
                        </div>

                        <div v-if="form.updateStatus" class="wh-active-toggle">
                            <label class="wh-toggle-wrap">
                                <input type="checkbox" v-model="form.data.active"/>
                                <span class="wh-toggle-pill"></span>
                                <span class="wh-toggle-text">
                                    <span class="wh-toggle-title">{{ $t('common.active') }}</span>
                                </span>
                            </label>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal" :disabled="form.loading">
                            {{ $t('common.cancel') }}
                        </button>
                        <button :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-primary" type="submit" :disabled="form.loading">
                            <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                            <span v-if="form.loading" class="indicator-progress">
                                {{ $t("messages.wait") }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import CustomTable from "@/components/custom-table";

export default {
    name: "warehouse",
    components: { CustomTable },
    data() {
        return {
            fields: [
                { name: this.$t("pages.stock.warehouse.cols.id"), key: "id", class: '' },
                { name: this.$t("pages.stock.warehouse.cols.name"), scopedSlots: { customRender: "name" } },
                { name: this.$t("pages.stock.warehouse.cols.shelves_count"), scopedSlots: { customRender: "shelves_count" } },
                { name: this.$t("pages.stock.warehouse.cols.active"), scopedSlots: { customRender: "active" } },
                { name: this.$t("common.action"), key: "action", scopedSlots: { customRender: "actions" } },
            ],
            actions: [
                { name: "new", icon: "bi-plus-lg", label: "btn.add" },
                { name: "refresh", icon: "bi-arrow-repeat", label: "btn.refresh" },
            ],
            form: {},
            filterParameters: {},
        }
    },
    computed: {
        table() {
            return this.$store.state.stock.warehouse.table;
        },
        saveData() {
            return this.$store.state.stock.warehouse.save.data;
        },
        canDelete() {
            return (this.table.data || []).length > 1;
        },
        defaultToggleDisabled() {
            if (!this.form.updateStatus) return false;
            if (!this.form.data?.is_default) return false;
            return (this.table.data || []).length <= 1;
        },
    },
    created() {
        this.resetForm();
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t("pages.stock.warehouse.title"), []);
        this.filterParameters = { sort: 'is_default:desc' };
        this.refreshTable();
    },
    methods: {
        onSubmit() {
            this.$refs.warehouseForm.validate((valid) => {
                if (!valid) return false;
                this.form.loading = true;
                const body = { name: this.form.data.name };
                if (this.form.updateStatus) {
                    body.is_default = !!this.form.data.is_default;
                    body.active = !!this.form.data.active;
                }

                const action = this.form.updateStatus
                    ? this.$store.dispatch('stock/warehouse/update', { id: this.form.data.id, formData: body })
                    : this.$store.dispatch('stock/warehouse/add', { formData: body });

                action.then(response => {
                    if (response?.status) {
                        this.onResponse(response, () => {
                            this.hideModal(this.$refs.warehouseModal);
                            this.refreshTable();
                        });
                    }
                    this.form.loading = false;
                });
            });
        },
        fetchWarehouse(record) {
            this.form = {
                title: this.sprintf(this.$t("pages.stock.warehouse.editWarehousePattern"), [record.name]),
                loading: false,
                updateStatus: true,
                data: {
                    id: record.id,
                    name: record.name,
                    is_default: !!record.is_default,
                    active: !!record.active,
                },
            };
            this.showModal(this.$refs.warehouseModal);
        },
        newWarehouse() {
            this.resetForm();
            this.form.title = this.$t("pages.stock.warehouse.newWarehouse");
            this.showModal(this.$refs.warehouseModal);
        },
        handleClickAction(name) {
            switch (name) {
                case "refresh":
                    this.refreshTable();
                    break;
                case "new":
                    this.newWarehouse();
                    break;
                default:
                    break;
            }
        },
        handleTableChange(pagination, filterData) {
            this.filterParameters = filterData;
            this.$store.dispatch("stock/warehouse/get", {
                page: pagination,
                filterData: this.filterParameters,
            });
        },
        deleteRecord(id) {
            this.$store.dispatch("stock/warehouse/delete", { id }).then(() => {
                this.refreshTable();
            });
        },
        refreshTable() {
            this.$store.dispatch('stock/warehouse/get', {
                page: { pageSize: 25, current: this.$route.query.page },
                filterData: Object.assign(this.filterParameters, this.$root.getFilterWithUrl({})),
            });
        },
        resetForm() {
            this.form = {
                title: '',
                loading: false,
                updateStatus: false,
                data: {
                    name: '',
                    is_default: false,
                    active: true,
                },
            };
        },
    },
}
</script>

<style scoped>
.wh-name-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 140px;
}

.wh-name { font-weight: 600; }

.wh-default-pill {
    display: inline-flex;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(var(--panel-accent-rgb, 0, 122, 255), 0.12);
    color: var(--panel-accent-text, #007AFF);
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.wh-count {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: #6b7280;
}

.wh-status {
    display: inline-flex;
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
}

.wh-status--on { background: rgba(40, 167, 69, 0.12); color: #28a745; }
.wh-status--off { background: rgba(107, 114, 128, 0.15); color: #6b7280; }

html.dark .wh-status--on,
html.midnight .wh-status--on { color: #34D399; }
html.dark .wh-status--off,
html.midnight .wh-status--off { color: #9CA3AF; }
html.dark .wh-count,
html.midnight .wh-count { color: #9A9CAE; }

.wh-action--disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
}


/* Form */
.wh-form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 14px;
}

.wh-form-label {
    font-size: 12px;
    font-weight: 600;
    color: #1c1c1e;
}

.wh-form-label--required::after {
    content: ' *';
    color: #ef4444;
}

html.dark .wh-form-label,
html.midnight .wh-form-label { color: #EAE6E1; }
html.cream .wh-form-label { color: #3D3832; }

.wh-default-toggle,
.wh-active-toggle {
    margin-top: 10px;
    padding: 12px 14px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.025);
    border: 1px solid rgba(0, 0, 0, 0.06);
}

html.dark .wh-default-toggle,
html.midnight .wh-default-toggle,
html.dark .wh-active-toggle,
html.midnight .wh-active-toggle {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.06);
}

html.cream .wh-default-toggle,
html.cream .wh-active-toggle {
    background: rgba(61, 56, 50, 0.04);
    border-color: rgba(61, 56, 50, 0.08);
}

.wh-toggle-wrap {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    width: 100%;
}

.wh-toggle-wrap--disabled { cursor: not-allowed; opacity: 0.7; }

.wh-toggle-wrap input { display: none; }

.wh-toggle-pill {
    flex-shrink: 0;
    width: 36px;
    height: 20px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.18);
    position: relative;
    transition: background 0.2s;
}

.wh-toggle-pill::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.18s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
}

.wh-toggle-wrap input:checked + .wh-toggle-pill {
    background: var(--panel-accent, #007AFF);
}

.wh-toggle-wrap input:checked + .wh-toggle-pill::after {
    transform: translateX(16px);
}

.wh-toggle-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.wh-toggle-title {
    font-size: 13px;
    font-weight: 600;
    color: #1c1c1e;
}

.wh-toggle-hint {
    font-size: 11px;
    color: #8e8e93;
}

html.dark .wh-toggle-title,
html.midnight .wh-toggle-title { color: #EAE6E1; }

html.dark .wh-toggle-hint,
html.midnight .wh-toggle-hint { color: #9A9CAE; }

html.cream .wh-toggle-title { color: #3D3832; }
html.cream .wh-toggle-hint { color: #847C72; }
</style>
